import React, {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useAsync, useLocation } from 'react-use';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import cn from 'classnames';
import { ArrayUtils, ObjectUtils } from 'src/utils';
import AppService from 'src/services/AppService';
import { trackingPage } from 'src/utils/tracking';
import { isIosSelector, isMiniappSelector } from 'src/selectors/app';
import { strUserIdSelector } from 'src/selectors/auth';
import {
    Container,
    Element,
    Image,
    RouterLink,
    Section,
    Title,
} from 'src/components';
import Card from 'src/components/card';
import { useTracking } from './useTracking';
import { ContentDescription } from 'src/modules/content/components/shared/ContentDescription';

interface IProps {
    title: string;
    size: number;
    categoryId: number;
    categoryIdPage: number;
    onShow?: any;
}
interface IZShortVideo {
    id: number;
    url: string;
    thumb: string;
    dynamicThumbUrl: string;
    thumbDynamicDuration: number;
    description: string;
    zaloIosScheme: string;
    zaloAndroidScheme: string;
    source: string;
    extra: string;
    author?: string;
    authorAvatar?: string;
}

export function ZShortMasthead(props: any) {
    const [show, onShow] = useState<boolean>(true);
    const location = useLocation();
    useEffect(() => {
        if ((window as any)._BM.showZVideo) {
            onShow(false);
        }
    }, [location.pathname]);

    if (!props.isZalo || ObjectUtils.isEmpty(props.ad)) {
        return null;
    }

    return (
        <Section
            className={cn(
                'min-h-[216px]',
                'border-b-4 border-solid border-[#EFEEF4]',
                'empty:hidden'
            )}
            id="js-zshort-masthead"
        >
            {show && (
                <Container
                    className={cn(
                        props.className,
                        '[font-family:var(--font-family-sans-serif)] ',
                        'py-[12px]'
                    )}
                >
                    <ZShortItems {...props} onShow={onShow} />
                </Container>
            )}
        </Section>
    );
}

function ZShortItems({
    title = 'Zalo Video',
    size,
    categoryId,
    categoryIdPage,
    onShow,
}: IProps) {
    const [currentPlay, setCurrentPlay] = useState<number>(-1);
    const [listInView, setListInView] = useState<number[]>([]);
    const state = useAsync(() => AppService.getZShorts(size, categoryId));
    const timeout = useRef<any>(0);
    const data = state.value?.data || {};
    const { items = [], ids = [] } = data;

    useEffect(() => {
        if (!ArrayUtils.isEmpty(listInView)) {
            if (currentPlay === -1) {
                setCurrentPlay(listInView[0]);
            }
        } else {
            setCurrentPlay(-1);
        }
    }, [listInView, currentPlay]);

    useEffect(() => {
        if (!ArrayUtils.isEmpty(listInView)) {
            setCurrentPlay(listInView[0]);
        }
    }, [listInView]);

    useEffect(() => {
        if (!state.loading) {
            const data = state.value?.data || {};
            const { items = [] } = data;
            if (items.length < 3) {
                onShow(false);
            } else {
                (window as any)._BM.showZVideo = true;
            }
        }
    }, [state]);

    const renderItem = (item: IZShortVideo, index: number) => {
        return (
            <React.Fragment key={index}>
                <ZShortItem
                    item={item}
                    ids={ids}
                    listInView={listInView}
                    isPlaying={currentPlay === item.id}
                    setCurrentPlay={setCurrentPlay}
                    setListInView={setListInView}
                    timeout={timeout}
                    categoryIdPage={categoryIdPage}
                />
            </React.Fragment>
        );
    };

    if (items.length < 3) {
        return null;
    }

    return (
        <>
            <Element
                className={cn(
                    'flex items-center',
                    'text-[#747474]',
                    'mb-[8px]',
                    'text-[1.3rem]'
                )}
            >
                <Element
                    className={cn(
                        'w-[24px] h-[24px]',
                        'mr-[8px]',
                        'bg-cover bg-center',
                        'bg-no-repeat',
                        'bg-[url(https://baomoi-static.bmcdn.me/wap/styles/img/event/zshort/logo_masthead.svg)]'
                    )}
                />
                <Title>{title}</Title>
                <Element className="mx-[4px]">·</Element>
                <Title renderAs="h4" className={cn('text-[#848485]')}>
                    Gợi ý cho bạn
                </Title>
            </Element>
            <Element className="ml-[-20px] w-[calc(100%+40px)]">
                <Element className="flex items-center overflow-auto [&::-webkit-scrollbar]:hidden px-[16px]">
                    {items.map((item: IZShortVideo, index: number) => {
                        return renderItem(item, index);
                    })}
                </Element>
            </Element>
        </>
    );
}

const ZShortItem = ({
    item,
    ids,
    setCurrentPlay,
    listInView = [],
    setListInView,
    isPlaying = false,
    timeout,
    categoryIdPage,
}: {
    item: IZShortVideo;
    ids: number[];
    setCurrentPlay: any;
    listInView: number[];
    setListInView: any;
    isPlaying: boolean;
    timeout?: any;
    categoryIdPage: number;
}) => {
    const isIOS = useSelector(isIosSelector);
    const strUserId = useSelector(strUserIdSelector);
    const isIframe = useSelector(isMiniappSelector);

    const source = `web_baomoi_masthead_c_${categoryIdPage}`;
    const [trackingRef, inView] = useTracking(item.id, source, item.extra);
    const url = isIOS
        ? item.zaloIosScheme + '?source=' + source
        : item.zaloAndroidScheme + '?source=' + source;

    useEffect(() => {
        if (inView) {
            if (listInView.indexOf(item.id) > -1) {
                return;
            }
            const index = ids.indexOf(item.id);
            setListInView((listInView: number[]) => {
                return index > listInView.length
                    ? [...listInView, item.id]
                    : [
                          ...listInView.slice(0, index),
                          item.id,
                          ...listInView.slice(index),
                      ];
            });
        } else {
            if (listInView.indexOf(item.id) > -1) {
                setListInView((listInView: number[]) =>
                    ArrayUtils.remove([...listInView], item.id)
                );
            }
        }
    }, [inView]);

    useEffect(() => {
        if (isPlaying && !ArrayUtils.isEmpty(listInView)) {
            timeout.current = setTimeout(() => {
                const nextItem = getNextIndex(item.id);
                setCurrentPlay(nextItem);
            }, item.thumbDynamicDuration * 2);
        }
        return () => {
            clearTimeout(timeout.current);
        };
    }, [isPlaying]);

    const getNextIndex = (id: number) => {
        const cIndex = listInView.indexOf(id);
        if (listInView[cIndex + 1]) {
            return listInView[cIndex + 1];
        }
        return listInView[0];
    };

    const handleClick = (evt: React.MouseEvent) => {
        evt.preventDefault();
        evt.stopPropagation();
        trackingPage('mobile_zshort', item.id, strUserId, source, item.extra);
        if (isIframe) {
            window.parent.postMessage(
                {
                    type: 'OPEN_LINK_FROM_BAOMOI',
                    url: item.url + '?source=' + item.source,
                },
                'https://h5.zdn.vn'
            );
        } else {
            window.open(url, '_blank');
        }
    };

    const objEllipsis = useMemo(() => {
        return {
            widthContainer: 88,
            sizeText: '12px',
            fontWeight: 'bold',
        };
    }, []);

    const renderItems = useCallback(() => {
        return (
            <Card
                className={cn(
                    'w-[104px] mx-[4px]',
                    'shrink-0',
                    'pointer-events-none'
                )}
                domRef={trackingRef}
            >
                <Card.Image
                    className={cn(
                        'h-[156px] rounded-[8px]',
                        'border border-solid border-[#E6E6E6]'
                    )}
                >
                    <Image
                        className={cn(
                            'after:pointer-events-none',
                            'after:absolute after:z-[1] after:w-full after:h-full after:bottom-0 after:left-0',
                            'after:bg-gradient-to-b after:from-[transparent] after:from-[0%] after:via-[rgba(0,0,0,0.05)] after:via-50%  after:to-[rgba(0,0,0,0.6)] to-[100%]'
                        )}
                        src={item.thumb}
                        alt={item.description}
                    />
                    <Image
                        className={twMerge(
                            'absolute top-0 bottom-0',
                            'opacity-0',
                            cn({
                                ['opacity-100']: isPlaying,
                            })
                        )}
                        src={item.dynamicThumbUrl}
                        alt={item.description}
                    />
                </Card.Image>
                <Card.Header className="absolute top-[6px] left-[6px]">
                    {item.authorAvatar && (
                        <Image
                            className={cn(
                                'w-[20px] h-[20px]',
                                'rounded-full',
                                'overflow-hidden',
                                'border-[0.5px] border-solid border-[#FDFCFF]',
                                'shadow-[0_0_3px_0_rgba(0,0,0,0.35)]'
                            )}
                            src={item.authorAvatar}
                            alt={item.description}
                        />
                    )}
                </Card.Header>
                <Card.Content className="absolute z-[1] right-[6px] left-[6px] bottom-[6px] text-white">
                    {item.author && (
                        <ContentDescription
                            objEllipsis={objEllipsis}
                            className="[display:-webkit-box] text-inherit font-bold text-[1.2rem] relative"
                            description={item.author}
                        />
                    )}
                </Card.Content>
            </Card>
        );
    }, [item, isPlaying]);

    return (
        <RouterLink to={url} newTab onClick={handleClick}>
            {renderItems()}
        </RouterLink>
    );
};

export default memo(ZShortMasthead);
